import type { ComponentType, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

export type BrowserFlowStep = {
  label: string
  instructions: string
  annotations?: string[]
}

interface BrowserFlowToolCardProps {
  title: string
  rationale: string
  icon: ComponentType<{ className?: string }>
  destinationLabel: ReactNode
  entryUrl?: string
  fallbackInstructions?: string
  showFallback: boolean
  steps: BrowserFlowStep[]
  warning?: ReactNode
  buttonIcon: ComponentType<{ className?: string }>
  buttonLabel: string
  loadingLabel: string
  installLabel: string
  isLoading: boolean
  disabled: boolean
  extensionInstalled: boolean | null
  onClick: () => void
}

export function BrowserFlowToolCard({
  title,
  rationale,
  icon: HeaderIcon,
  destinationLabel,
  entryUrl,
  fallbackInstructions,
  showFallback,
  steps,
  warning,
  buttonIcon: ButtonIcon,
  buttonLabel,
  loadingLabel,
  installLabel,
  isLoading,
  disabled,
  extensionInstalled,
  onClick,
}: BrowserFlowToolCardProps) {
  return (
    <div className='mb-2.5 w-full overflow-hidden rounded-md border border-border bg-card last:mb-0'>
      <div className='flex items-start justify-between gap-3 px-4 pt-3 pb-2'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <HeaderIcon className='h-3.5 w-3.5 shrink-0 text-text-secondary' />
            <div className='text-[12px] leading-[1.45] font-semibold break-words text-text-secondary'>
              {title}
            </div>
          </div>
          <div className='mt-2 text-[11px] leading-[1.45] text-text-body'>
            {rationale}
          </div>
        </div>
      </div>
      <div className='space-y-2 px-4 py-2'>
        <div className='rounded-md bg-surface-subtle px-3 py-2'>
          <div className='text-[10px] font-semibold tracking-[0.08em] text-text-subtle uppercase'>
            Destination
          </div>
          <div className='mt-1 text-[11px] font-medium break-words text-text-secondary'>
            {destinationLabel}
          </div>
        </div>
        {entryUrl && (
          <div className='rounded-md bg-surface-subtle px-3 py-2'>
            <div className='text-[10px] font-semibold tracking-[0.08em] text-text-subtle uppercase'>
              Start from
            </div>
            <a
              href={entryUrl}
              target='_blank'
              rel='noreferrer'
              className='mt-1 block text-[11px] leading-[1.45] break-all text-cobalt-strong hover:underline'
            >
              {entryUrl}
            </a>
          </div>
        )}
        {warning}
        {showFallback && fallbackInstructions && (
          <div className='rounded-md bg-surface-subtle px-3 py-2 text-[12px] leading-[1.45] text-text-body'>
            {fallbackInstructions}
          </div>
        )}
        <div className='rounded-md bg-surface-subtle px-3 py-1.5'>
          {steps.map((step, index) => (
            <div
              key={`${step.label}-${index}`}
              className=' py-2'
            >
              <div className='text-[12px] font-semibold text-text-secondary'>
                {index + 1}. {step.label}
              </div>
              <div className='mt-1 text-[12px] leading-[1.45] text-text-body'>
                {step.instructions}
              </div>
              {step.annotations && step.annotations.length > 0 && (
                <ul className='mt-2 space-y-1 pl-4 text-[12px] leading-[1.35] text-text-subtle'>
                  {step.annotations.map((annotation) => (
                    <li key={annotation} className='list-disc'>
                      {annotation}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className='flex items-center justify-end gap-2 px-3 py-2'>
        <button
          type='button'
          onClick={onClick}
          disabled={disabled}
          className='inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {isLoading ? (
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
          ) : (
            <ButtonIcon className='h-3.5 w-3.5' />
          )}
          {isLoading
            ? loadingLabel
            : extensionInstalled === false
              ? installLabel
              : buttonLabel}
        </button>
      </div>
    </div>
  )
}
